/**
 * AcceleratorQualification
 *
 * Tells an Accelerator Application applicant as soon as an answer rules them
 * out, instead of letting them fill the rest of the form first. The rules are
 * handed down from PHP (window.tmAcceleratorQualification) so this never drifts
 * from the server-side guard, which remains the one that actually blocks the
 * submission.
 */
class AcceleratorQualification {
  /**
   * @param {HTMLFormElement} form
   * @param {{formId: number, summary: string, rules: Array<Object>}} config
   */
  constructor(form, config) {
    this.form = form;
    this.rules = config.rules || [];
    this.summary = config.summary || "";
    this.notice = null;
    this.bind();
    this.evaluate();
  }

  /**
   * Watch the fields the rules depend on. Forminator dresses its selects with
   * select2, which fires change through jQuery only — a native listener would
   * never see it — so prefer jQuery when it is on the page.
   */
  bind() {
    const onChange = () => this.evaluate();

    if (window.jQuery) {
      // jQuery handlers see native events too, so this covers both cases.
      window.jQuery(this.form).on("change.tmQualification", onChange);
    } else {
      this.form.addEventListener("change", onChange);
    }

    this.form.addEventListener("click", (e) => this.onClick(e), true);
  }

  /**
   * Stop a submit attempt while the applicant is disqualified.
   * @param {MouseEvent} e
   */
  onClick(e) {
    if (!e.target.closest(".forminator-button-submit")) return;
    if (!this.evaluate().length) return;

    e.preventDefault();
    e.stopImmediatePropagation();
    this.scrollToNotice();
  }

  /**
   * Run every rule and render the notice.
   * @returns {Array<Object>} The rules the current answers fail.
   */
  evaluate() {
    const failures = this.rules.filter((rule) => !this.passes(rule));
    this.render(failures);
    return failures;
  }

  /**
   * Test one answer against one rule. An unanswered field passes, so the
   * applicant is not told off before they have had a chance to answer.
   * @param {Object} rule
   * @returns {boolean}
   */
  passes(rule) {
    const value = this.getValue(rule.field);
    if (value === "") return true;

    if (rule.test === "min") {
      const number = parseFloat(value.replace(/[^0-9.\-]/g, ""));
      return !isNaN(number) && number >= parseFloat(rule.value);
    }

    return value === rule.value;
  }

  /**
   * @param {string} field Forminator element ID, e.g. "select-2".
   * @returns {string} The submitted value, or "" when unanswered.
   */
  getValue(field) {
    const input = this.form.querySelector(`[name="${field}"]`);
    return input && input.value ? input.value.trim() : "";
  }

  /**
   * Show, update, or hide the notice, and flag the offending fields.
   * @param {Array<Object>} failures
   */
  render(failures) {
    this.form
      .querySelectorAll(".forminator-field.is-disqualifying")
      .forEach((field) => field.classList.remove("is-disqualifying"));

    failures.forEach((rule) => {
      const input = this.form.querySelector(`[name="${rule.field}"]`);
      const field = input && input.closest(".forminator-field");
      if (field) field.classList.add("is-disqualifying");
    });

    if (!failures.length) {
      if (this.notice) this.notice.hidden = true;
      return;
    }

    const notice = this.getNotice();
    const list = notice.querySelector(".qualification-notice__list");

    list.textContent = "";
    failures.forEach((rule) => {
      const item = document.createElement("li");
      item.className = "qualification-notice__item";
      item.textContent = rule.message;
      list.appendChild(item);
    });

    notice.hidden = false;
  }

  /**
   * Build the notice on first use and park it just above the submit button.
   * @returns {HTMLElement}
   */
  getNotice() {
    if (this.notice) return this.notice;

    const notice = document.createElement("div");
    notice.className = "qualification-notice";
    notice.setAttribute("role", "alert");
    notice.innerHTML =
      '<p class="qualification-notice__title"></p>' +
      '<ul class="qualification-notice__list"></ul>';
    notice.querySelector(".qualification-notice__title").textContent =
      this.summary;

    const submit = this.form.querySelector(".forminator-button-submit");
    const row = submit && submit.closest(".forminator-row");

    if (row) {
      row.parentNode.insertBefore(notice, row);
    } else {
      this.form.appendChild(notice);
    }

    this.notice = notice;
    return notice;
  }

  /**
   * Bring the notice into view after a blocked submit attempt.
   */
  scrollToNotice() {
    if (!this.notice) return;
    this.notice.scrollIntoView({ behavior: "smooth", block: "center" });
  }
}

/**
 * Boot the guard on every rendered copy of the configured form.
 */
export default () => {
  const config = window.tmAcceleratorQualification;
  if (!config || !config.formId) return;

  document
    .querySelectorAll(`form#forminator-module-${config.formId}`)
    .forEach((form) => new AcceleratorQualification(form, config));
};
